import { getRateLimitUntil, isRateLimited, retryAfterSeconds } from './rateLimit';

/** Serialized, read-only background polling. A foreground mutation is never retried here. */
export function startBackgroundPoll(task: () => Promise<void>, intervalMs = 60_000) {
  let stopped = false;
  let running = false;
  let nextAllowedAt = 0;
  let failures = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const schedule = (delay: number) => {
    if (stopped) return;
    clearTimeout(timer);
    timer = setTimeout(run, delay);
  };
  const run = async () => {
    if (stopped || running) return;
    clearTimeout(timer);
    const wait = Math.max(nextAllowedAt, getRateLimitUntil()) - Date.now();
    if (wait > 0) { schedule(wait); return; }
    if (document.hidden) { schedule(intervalMs); return; }
    running = true;
    let delay = intervalMs;
    try {
      await task();
      failures = 0;
    } catch (error) {
      if (isRateLimited(error)) {
        failures = Math.min(failures + 1, 4);
        delay = Math.max(retryAfterSeconds(error) * 1000, Math.min(900_000, intervalMs * 2 ** failures));
        nextAllowedAt = Date.now() + delay;
      }
      // Other transient failures resume at the normal interval, without a tight retry loop.
    } finally {
      running = false;
      schedule(delay);
    }
  };
  const onVisible = () => { if (!document.hidden) void run(); };
  document.addEventListener('visibilitychange', onVisible);
  void run();
  return () => {
    stopped = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
